import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "../ui/Card.tsx";
import { Button } from "../ui/Button.tsx";
import { Input } from "../ui/Input.tsx";
import {
  Database,
  Lock,
  Pencil,
  Plus,
  Search,
  Shield,
  Trash2,
  Users,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../../apiWithAccessToken";
import PolicyModal from "./rls/PolicyModal";
import SkeletonLoader from "../ui/SkeletonLoader";

const rowsPerPage = 6;

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
};

const LIST_OPS = new Set(["in", "not_in"]);

const toRuleValue = (operator, rawValue) => {
  const op = String(operator || "").toLowerCase();
  if (rawValue === undefined || rawValue === null) return LIST_OPS.has(op) || op === "between" ? [] : "";
  if (LIST_OPS.has(op)) {
    if (Array.isArray(rawValue)) return rawValue.map((v) => String(v));
    return String(rawValue)
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  if (op === "between") {
    if (Array.isArray(rawValue)) return [rawValue[0] ?? "", rawValue[1] ?? ""];
    const parts = String(rawValue)
      .split(",")
      .map((v) => v.trim())
      .slice(0, 2);
    return [parts[0] || "", parts[1] || ""];
  }
  if (typeof rawValue === "object") return JSON.stringify(rawValue);
  return String(rawValue);
};

const ruleJsonToForm = (ruleJson) => {
  const op = String(ruleJson?.op || "and").toLowerCase();
  if (op === "and" || op === "or") {
    return {
      logicalOp: op,
      conditions: (ruleJson?.conditions || []).map((c, idx) => ({
        id: `rr_${idx}_${Date.now()}`,
        column: c.column || "",
        operator: c.op || "eq",
        value: toRuleValue(c.op, c.value),
      })),
    };
  }
  return {
    logicalOp: "and",
    conditions: [
      {
        id: `rr_0_${Date.now()}`,
        column: ruleJson?.column || "",
        operator: ruleJson?.op || "eq",
        value: toRuleValue(ruleJson?.op, ruleJson?.value),
      },
    ],
  };
};

const derivePolicyKind = ({ hasRls, hasMasking }) => {
  if (hasRls && hasMasking) return "combined";
  if (hasMasking) return "masking";
  if (hasRls) return "rls";
  return "disabled";
};

const mapPolicyApiToUi = (policy, usersMap, groupsMap, dbMap) => {
  const rowRules = Array.isArray(policy?.row_rules) ? policy.row_rules : [];
  const maskRules = Array.isArray(policy?.mask_rules) ? policy.mask_rules : [];
  const tableName = policy?.table_name || rowRules[0]?.table_name || maskRules[0]?.table_name || "";
  const rlsForm = rowRules[0]?.rule_json ? ruleJsonToForm(rowRules[0].rule_json) : { logicalOp: "and", conditions: [] };
  const hasRls = typeof policy?.has_rls === "boolean" ? policy.has_rls : rowRules.some((rule) => rule?.is_active !== false);
  const hasMasking =
    typeof policy?.has_masking === "boolean" ? policy.has_masking : maskRules.some((rule) => rule?.is_active !== false);
  const rowRuleCount = Number(policy?.row_rule_count ?? rowRules.filter((rule) => rule?.is_active !== false).length);
  const maskRuleCount = Number(policy?.mask_rule_count ?? maskRules.filter((rule) => rule?.is_active !== false).length);
  const policyType = derivePolicyKind({ hasRls, hasMasking });

  return {
    id: policy.id,
    policyId: policy.id,
    name: policy.policy_name || "",
    scope: policy.principal_type,
    principalId: policy.principal_id,
    principalLabel:
      policy.principal_type === "user"
        ? usersMap.get(policy.principal_id) || `User ${policy.principal_id}`
        : groupsMap.get(policy.principal_id) || `Group ${policy.principal_id}`,
    databaseId: policy.source_db_config_id,
    databaseLabel: dbMap.get(policy.source_db_config_id) || `DB ${policy.source_db_config_id}`,
    tableName,
    policyType,
    hasRls,
    hasMasking,
    ruleCount: policyType === "combined" ? rowRuleCount + maskRuleCount : policyType === "masking" ? maskRuleCount : rowRuleCount,
    rowRuleCount,
    maskRuleCount,
    rlsLogicalOp: rlsForm.logicalOp,
    rlsRules: rlsForm.conditions,
    maskRules: maskRules.map((m, idx) => ({
      id: m.id || `mr_${idx}_${Date.now()}`,
      column: m.column_name || "",
      maskType: m.mask_type || "token",
      maskValue:
        m.mask_params_json?.replacement !== undefined
          ? String(m.mask_params_json.replacement)
          : m.mask_params_json?.value !== undefined
          ? String(m.mask_params_json.value)
          : "",
    })),
    updatedAt: policy.modified_date || policy.created_date,
  };
};

const mapTableViewApiToUi = (row, usersMap, groupsMap, dbMap) => ({
  id: row.policy_id,
  policyId: row.policy_id,
  name: row.policy_name || "",
  scope: row.principal_type,
  principalId: row.principal_id,
  principalLabel:
    row.principal_type === "user"
      ? usersMap.get(row.principal_id) || `User ${row.principal_id}`
      : groupsMap.get(row.principal_id) || `Group ${row.principal_id}`,
  databaseId: row.source_db_config_id,
  databaseLabel: dbMap.get(row.source_db_config_id) || `DB ${row.source_db_config_id}`,
  tableName: row.table_name || "",
  policyType: derivePolicyKind({
    hasRls: !!row.has_rls,
    hasMasking: !!row.has_masking,
  }),
  hasRls: !!row.has_rls,
  hasMasking: !!row.has_masking,
  ruleCount: Number(row.rule_count || 0),
  rowRuleCount: Number(row.row_rule_count || 0),
  maskRuleCount: Number(row.mask_rule_count || 0),
  updatedAt: row.modified_date,
});

function TypeBadge({ policyType }) {
  const kind = String(policyType || "").toLowerCase();
  const badgeClassName = (tone) =>
    `inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${tone}`;

  if (kind === "combined") {
    return (
      <div className="inline-flex items-center gap-1.5 flex-wrap">
        <span className={badgeClassName("text-blue-700 bg-blue-50 border-blue-200")}>
          <Shield size={10} />
          RLS
        </span>
        <span className={badgeClassName("text-purple-700 bg-purple-50 border-purple-200")}>
          <Lock size={10} />
          Masking
        </span>
      </div>
    );
  }

  return (
    <span
      className={badgeClassName(
        kind === "combined"
          ? "text-emerald-700 bg-emerald-50 border-emerald-200"
          : kind === "disabled"
          ? "text-slate-600 bg-slate-50 border-slate-200"
          : kind === "masking"
          ? "text-purple-700 bg-purple-50 border-purple-200"
          : "text-blue-700 bg-blue-50 border-blue-200"
      )}
    >
      {kind === "masking" ? (
        <>
          <Lock size={10} />
          Masking
        </>
      ) : kind === "disabled" ? (
        <>
          <Lock size={10} />
          Disabled
        </>
      ) : (
        <>
          <Shield size={10} />
          RLS
        </>
      )}
    </span>
  );
}

export default function RLSPolicyManagement() {
  const [policies, setPolicies] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersResp, groupsResp, dbResp, policyResp] = await Promise.all([
        api.get("/users/"),
        api.get("/groups/"),
        api.get("/users/db-options"),
        api.get("/security/policies/table-view", { params: { include_inactive: true } }),
      ]);

      const rawUsers = usersResp.data?.users || usersResp.data || [];
      const rawGroups = groupsResp.data || [];
      const rawPolicies = policyResp.data?.data || [];
      const dbOptions = dbResp.data || [];

      const usersList = rawUsers.map((u) => ({ id: u.id, name: u.name, email: u.email }));
      const groupsList = rawGroups.map((g) => ({ id: g.id, name: g.name }));
      const usersMap = new Map(usersList.map((u) => [u.id, u.name]));
      const groupsMap = new Map(groupsList.map((g) => [g.id, g.name]));
      const dbLabelMap = new Map(dbOptions.map((d) => [d.id, d.display_label || d.title || `DB ${d.id}`]));

      setUsers(usersList);
      setGroups(groupsList);
      setPolicies(rawPolicies.map((p) => mapTableViewApiToUi(p, usersMap, groupsMap, dbLabelMap)));
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to load security policy data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredPolicies = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return policies;
    return policies.filter((p) =>
      [
        p.name,
        p.principalLabel,
        p.databaseLabel,
        p.tableName,
        p.scope,
        p.policyType,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [policies, searchTerm]);

  const totalPages = Math.ceil(filteredPolicies.length / rowsPerPage) || 1;
  const paginated = filteredPolicies.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const openCreate = () => {
    setEditingPolicy(null);
    setShowModal(true);
  };

  const openEdit = async (policy) => {
    try {
      const { data } = await api.get(`/security/policies/${policy.policyId}`);
      const full = data?.data;
      if (!full) {
        toast.error("Policy not found");
        return;
      }
      const mapped = mapPolicyApiToUi(
        full,
        new Map(users.map((u) => [u.id, u.name])),
        new Map(groups.map((g) => [g.id, g.name])),
        new Map(policies.map((p) => [p.databaseId, p.databaseLabel]))
      );
      setEditingPolicy(mapped);
      setShowModal(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to load policy details");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPolicy(null);
  };

  const onSave = async ({ id, request }) => {
    try {
      let effectiveAction = id ? "updated" : "created";
      if (id) {
        const updatePayload = { ...request };
        // Keep identity immutable on edit.
        delete updatePayload.source_db_config_id;
        delete updatePayload.principal_type;
        delete updatePayload.principal_id;
        delete updatePayload.table_name;
        await api.put(`/security/policies/${id}`, updatePayload);
      } else {
        await api.post("/security/policies", request);
      }
      toast.success(effectiveAction === "updated" ? "Policy updated" : "Policy created");
      closeModal();
      await loadData();
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      const msg = Array.isArray(apiErrors) && apiErrors.length > 0 ? apiErrors[0] : err?.response?.data?.error || "Failed to save policy";
      toast.error(msg);
    }
  };

  const onDelete = async (policy) => {
    if (!window.confirm(`Delete policy "${policy.name}"?`)) return;
    try {
      await api.delete(`/security/policies/${policy.policyId}`, {
        data: {
          reason: "Deleted from RLS Policy screen",
        },
      });
      toast.success("Policy deleted");
      await loadData();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to delete policy");
    }
  };

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-foreground flex items-center gap-2">
          <Lock className="w-4 h-4 text-primary" />
          Security Policy
        </h1>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search policies..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10 h-9"
            />
          </div>
          <Button onClick={openCreate} className="h-9 px-4 flex items-center gap-2 whitespace-nowrap">
            <Plus size={14} />
            Add Security Policy
          </Button>
        </div>
      </div>

      <div className="hidden md:block bg-background">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-20 bg-background shadow-sm">
            <tr className="text-left text-muted-foreground font-medium">
              <th className="px-4 py-2">Policy Name</th><th className="px-4 py-2">Scope</th><th className="px-4 py-2">Principal</th><th className="px-4 py-2">Type</th><th className="px-4 py-2">Database</th><th className="px-4 py-2">Table</th><th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading &&
              Array.from({ length: rowsPerPage }).map((_, idx) => (
                <tr key={`loading-row-${idx}`}>
                  <td className="px-4 py-2">
                    <SkeletonLoader className="h-4 w-40 mb-2" />
                    <SkeletonLoader className="h-3 w-28" />
                  </td>
                  <td className="px-4 py-2">
                    <SkeletonLoader className="h-4 w-16" />
                  </td>
                  <td className="px-4 py-2">
                    <SkeletonLoader className="h-4 w-28" />
                  </td>
                  <td className="px-4 py-2">
                    <SkeletonLoader className="h-5 w-16 rounded-full" />
                  </td>
                  <td className="px-4 py-2">
                    <SkeletonLoader className="h-4 w-24" />
                  </td>
                  <td className="px-4 py-2">
                    <SkeletonLoader className="h-4 w-32" />
                  </td>
                  <td className="px-4 py-2 text-right">
                    <SkeletonLoader className="h-6 w-16 ml-auto" />
                  </td>
                </tr>
              ))}
            {!loading && paginated.map((policy) => (
              <tr key={policy.id} className="hover:bg-muted/40 transition-colors">
                <td className="px-4 py-2"><div className="font-medium text-foreground">{policy.name}</div></td>
                <td className="px-4 py-2"><span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Users size={12} />{policy.scope === "user" ? "User" : "Group"}</span></td>
                <td className="px-4 py-2 text-xs">{policy.principalLabel}</td>
                <td className="px-4 py-2"><TypeBadge policyType={policy.policyType} /></td>
                <td className="px-4 py-2 text-xs">{policy.databaseLabel}</td>
                <td className="px-4 py-2 text-xs">{policy.tableName} {policy.ruleCount ? `(${policy.ruleCount})` : ""}</td>
                <td className="px-4 py-2 text-right"><div className="inline-flex items-center gap-1"><Button size="icon" className="h-8 w-8" variant="ghost" onClick={() => openEdit(policy)}><Pencil size={14} /></Button><Button size="icon" className="h-8 w-8" variant="ghost" onClick={() => onDelete(policy)}><Trash2 size={14} /></Button></div></td>
              </tr>
            ))}
            {!loading && paginated.length === 0 && (<tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">No policies found</td></tr>)}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3">
        {loading ? (
          Array.from({ length: 3 }).map((_, idx) => (
            <Card key={`mobile-loading-${idx}`} className="p-4 border border-border">
              <SkeletonLoader className="h-4 w-36 mb-2" />
              <SkeletonLoader className="h-3 w-24 mb-1.5" />
              <SkeletonLoader className="h-3 w-40 mb-3" />
              <SkeletonLoader className="h-8 w-20 ml-auto" />
            </Card>
          ))
        ) : paginated.length === 0 ? (
          <Card className="p-4 border border-border text-sm text-muted-foreground text-center">No policies found</Card>
        ) : (
          paginated.map((policy) => (
            <Card key={policy.id} className="p-4 border border-border">
              <div className="flex items-center justify-between gap-2"><div className="font-medium text-sm">{policy.name}</div><TypeBadge policyType={policy.policyType} /></div>
              <div className="mt-2 text-xs text-muted-foreground space-y-1">
                <div>Scope: {policy.scope === "user" ? "User" : "Group"}</div><div>Principal: {policy.principalLabel}</div><div className="inline-flex items-center gap-1"><Database size={12} /> {policy.databaseLabel} / {policy.tableName}</div><div>Updated: {formatDate(policy.updatedAt)}</div>
              </div>
              <div className="mt-3 flex justify-end gap-2"><Button size="icon" variant="ghost" onClick={() => openEdit(policy)}><Pencil size={14} /></Button><Button size="icon" variant="ghost" onClick={() => onDelete(policy)}><Trash2 size={14} /></Button></div>
            </Card>
          ))
        )}
      </div>

      <div className="flex items-center justify-between mt-2 shrink-0 px-1">
        <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">{filteredPolicies.length}</span> of {policies.length} policies</p>
        {totalPages > 1 && (
          <div className="flex items-center gap-3 bg-muted/40 px-3 py-1.5 rounded-lg">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} className={`flex items-center justify-center w-7 h-7 rounded-md transition-all ${currentPage === 1 ? "text-muted-foreground opacity-40 cursor-not-allowed" : "hover:bg-primary/10 hover:text-primary"}`}><ChevronLeft className="h-4 w-4" /></button>
            <span className="text-xs font-medium text-muted-foreground tracking-wide">{currentPage} / {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} className={`flex items-center justify-center w-7 h-7 rounded-md transition-all ${currentPage === totalPages ? "text-muted-foreground opacity-40 cursor-not-allowed" : "hover:bg-primary/10 hover:text-primary"}`}><ChevronRight className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      <PolicyModal open={showModal} mode={editingPolicy ? "edit" : "create"} initialPolicy={editingPolicy} users={users} groups={groups} onClose={closeModal} onSave={onSave} />
    </div>
  );
}
